import { useState, useRef, useEffect, ReactNode, KeyboardEventHandler } from 'react';
import { Button } from 'src/components/ui';
import { getTestId } from '../hooks/useTestId';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Notification from '../components/Notification';
import MessageBubble, { groupMessages } from './MessageBubble';
import InlineCompose from './InlineCompose';
import { DataRemarkMessage, MAX_DATA_REMARK_MESSAGE_LENGTH } from '../api/DataRemarksApi';
import { WFAccessToken } from '../api/types';
import Icon from 'src/components/Icon';

const PREVIEW_COUNT = 3;

type MessageThreadProps = {
  messages: DataRemarkMessage[];
  currentUserId?: number;
  /** ID of the user who created the remark — drives empty-state behaviour. */
  remarkCreatedById?: number | null;
  /** True when the current user is in "response" mode (responder, not reviewer). */
  responseMode?: boolean;
  wfAccessToken?: WFAccessToken;
  /** When provided, enables the compose area. Omit for read-only. */
  onSend?: (payload: { message: string; file: File | null }) => Promise<void>;
  /** Overrides the default "Messages" label when there are ≤3 messages. */
  label?: ReactNode;
  composeRows?: number;
  /** When this transitions to false, internal compose state is reset. */
  isOpen?: boolean;
  /** When true, hides the compose area entirely regardless of other props. */
  readonly?: boolean;
};

export default function MessageThread({
  messages,
  currentUserId,
  remarkCreatedById,
  responseMode = false,
  wfAccessToken,
  onSend,
  label,
  composeRows = 2,
  isOpen,
  readonly = false,
}: MessageThreadProps) {
  const [threadExpanded, setThreadExpanded] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageFile, setMessageFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const olderRef = useRef<HTMLDivElement>(null);

  const hasMessages = messages.length > 0;
  const hiddenCount = Math.max(0, messages.length - PREVIEW_COUNT);
  const previewMessages = messages.slice(-PREVIEW_COUNT);

  // Is the logged-in user the one who created this remark?
  const isOwnRemark = currentUserId != null && remarkCreatedById != null && currentUserId === remarkCreatedById;

  // Show the compose area when:
  //   - There ARE messages already (always allow reply regardless of mode/ownership)
  //   - OR: no messages yet, different user, response mode
  const showCompose = !readonly && onSend != null && (hasMessages || (!isOwnRemark && responseMode));

  // Show the "Last messages / All messages" title only when messages exist
  const showTitle = hasMessages;

  useEffect(() => {
    if (threadExpanded && olderRef.current) {
      olderRef.current.scrollTop = olderRef.current.scrollHeight;
    }
  }, [threadExpanded, messages.length]);

  useEffect(() => {
    if (isOpen === false) {
      setThreadExpanded(false);
      setMessageText('');
      setMessageFile(null);
      setSendError(null);
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!messageText.trim() || messageText.length > MAX_DATA_REMARK_MESSAGE_LENGTH || !onSend) return;
    setSending(true);
    setSendError(null);
    try {
      await onSend({ message: messageText.trim(), file: messageFile });
      setMessageText('');
      setMessageFile(null);
    } catch (err) {
      setSendError(err?.message || err?.result || t`Failed to send message`);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown: KeyboardEventHandler = e => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend();
  };

  const effectiveLabel =
    hiddenCount > 0 ? threadExpanded ? <Trans>All messages</Trans> : <Trans>Last messages</Trans> : label ?? <Trans>Messages</Trans>;

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (!hasMessages) {
    return (
      <div>
        {!isOwnRemark && responseMode ? (
          <>
            <p className="small text-muted mb-2">
              <Trans>
                Respond with a detailed justification to this flag. Provide any evidence if it is relevant by attaching a file to the message.
              </Trans>
            </p>
            {showCompose && (
              <div>
                {sendError && <Notification color="danger">{sendError}</Notification>}
                <InlineCompose
                  value={messageText}
                  onChange={e => setMessageText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t`Write a message… (Ctrl+Enter to send)`}
                  rows={composeRows}
                  file={messageFile}
                  onFileSelected={setMessageFile}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  maxLength={MAX_DATA_REMARK_MESSAGE_LENGTH}
                />
                <div className="flex justify-end mt-2">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleSend}
                    disabled={!messageText.trim() || messageText.length > MAX_DATA_REMARK_MESSAGE_LENGTH || sending}
                    className="flex items-center gap-1"
                    {...getTestId('message-thread', '.', 'send')}
                  >
                    <Icon icon="PaperPlaneRight" className="mr-1" />
                    <Trans>Save</Trans>
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-muted mt-4 mb-2">
            <i>
              <Trans>
                The institution has not responded to this flag yet.
                <br />
                The response will appear here when they submit one.
              </Trans>
            </i>
          </p>
        )}
      </div>
    );
  }

  // ── Messages present ─────────────────────────────────────────────────────────
  return (
    <div>
      {showTitle && (
        <div className="flex items-center gap-2 mb-2">
          <span className="small-label mb-0">{effectiveLabel}</span>
          {hiddenCount > 0 && (
            <Button
              color="link"
              size="sm"
              className="p-0 ml-1 small"
              onClick={() => setThreadExpanded(e => !e)}
              {...getTestId('message-thread', '.', 'toggle-expand')}
            >
              {threadExpanded ? (
                <>
                  <Icon icon="CaretUp" className="mr-1" />
                  <Trans>Collapse</Trans>
                </>
              ) : (
                <>
                  <Icon icon="CaretDown" className="mr-1" />
                  <Trans>Show all (+{hiddenCount} more)</Trans>
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Collapsed: last 3 preview. Expanded: all messages in one scrollable container. */}
      {threadExpanded ? (
        <div ref={olderRef} className="overflow-auto mb-2" style={{ maxHeight: 400 }}>
          {groupMessages(messages).map(group => (
            <MessageBubble key={group[0].id} messages={group} currentUserId={currentUserId} wfAccessToken={wfAccessToken} />
          ))}
        </div>
      ) : (
        <div className="mb-2">
          {groupMessages(previewMessages).map(group => (
            <MessageBubble key={group[0].id} messages={group} currentUserId={currentUserId} wfAccessToken={wfAccessToken} />
          ))}
        </div>
      )}

      {showCompose && (
        <div>
          {sendError && <Notification color="danger">{sendError}</Notification>}
          <InlineCompose
            value={messageText}
            onChange={e => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t`Write a message… (Ctrl+Enter to send)`}
            rows={composeRows}
            file={messageFile}
            onFileSelected={setMessageFile}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            maxLength={MAX_DATA_REMARK_MESSAGE_LENGTH}
          />
          <div className="flex mt-1" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <small>
              <Trans>
                If this notification is in the "Responded" state, adding a comment will automatically return it to "Active". To add internal notes
                that don't affect the status, use the Notes panel.
              </Trans>
            </small>
            <div className="flex justify-end mt-2">
              <Button
                color="primary"
                size="sm"
                onClick={handleSend}
                disabled={!messageText.trim() || messageText.length > MAX_DATA_REMARK_MESSAGE_LENGTH || sending}
                className="flex items-center gap-1"
                {...getTestId('message-thread', '.', 'send')}
              >
                <Icon icon="PaperPlaneRight" className="mr-1" />
                <Trans>Save</Trans>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
